import React from 'react';
import _isMatch from 'underscore/modules/isMatch';

import Service from '../core/Service';
import ServiceProvider from '../core/ServiceProvider';
import { injectedService } from '@base/core';
import {
  isInsideTgMiniApp,
  isVkOAuthCallbackWithParams,
  isVkOAuthLinkFlow,
} from '@base/utils/utils';
import { clearAppStorageKeys } from '../hooks/useSessionStorage';
import { AUTH_TOKEN_STORAGE_KEY, AUTH_TOKEN_TS_STORAGE_KEY } from '../constants/authStorage';

const TOKEN_KEY = AUTH_TOKEN_STORAGE_KEY;
const TOKEN_TS_KEY = AUTH_TOKEN_TS_STORAGE_KEY;
const TOKEN_TTL = 6 * 60 * 60 * 1000; // ms, 6 hour;

const removeUrlTokens = () => {
  const url = new URL(window.location.href);
  url.searchParams.delete('token');
  url.searchParams.delete('accessToken');
  window.history.replaceState({}, '', url.toString());
};

const processCollision = async (access, urlToken, storedToken) => {
  try {
    const urlTokenAccess = await access.api.isLoggedIn({ options: { authToken: urlToken } });
    const storedTokenAccess = await access.api.isLoggedIn({ options: { authToken: storedToken } });
    if (urlTokenAccess.memberId === storedTokenAccess.memberId) {
      if (urlTokenAccess.fullMemberAccess) {
        access.login(urlToken, urlTokenAccess);
      } else if (storedTokenAccess.fullMemberAccess) {
        access.login(storedToken, storedTokenAccess);
      } else {
        access.login(urlToken, urlTokenAccess);
      }
    } else {
      access.login(urlToken, urlTokenAccess);
    }
  } catch (e) {
    access.updateState({
      tokenDetected: false,
      detected: true,
      memberId: null,
      isLogged: false,
      fullMemberAccess: false,
      viewCabinetMemberAccess: false,
    });
  } finally {
    removeUrlTokens();
  }
};

export class AccessService extends Service {
  // dependencies
  api = null; // ApiService
  onAccessChange = null;
  tokenTTL = null; // ms, if null uses default TOKEN_TTL

  // properties
  tokenDetected = false;
  detected = false;
  memberId = null;
  isLogged = false;
  fullMemberAccess = false;
  error = null;
  viewCabinetMemberAccess = false;

  initState(dependencies, refs) {
    super.initState(dependencies, refs);
    const searchParams = new URLSearchParams(window.location.search);
    const urlToken = searchParams.get('token') || searchParams.get('accessToken');
    const storedToken = localStorage.getItem(TOKEN_KEY);
    const tokenTTL = this.tokenTTL ?? TOKEN_TTL;
    
    if (urlToken && storedToken && urlToken !== storedToken) {
      this.tokenDetected = true;
      processCollision(this, urlToken, storedToken);
    } else {
      const tsNow = Date.now();
      if (urlToken) {
        clearAppStorageKeys();
        localStorage.setItem(TOKEN_KEY, urlToken);
        localStorage.setItem(TOKEN_TS_KEY, tsNow);
        this.tokenDetected = true;
        this._setToken(urlToken);
        removeUrlTokens();
      } else if (storedToken) {
        const storedTokenTS = localStorage.getItem(TOKEN_TS_KEY);
        if (storedTokenTS && tsNow - storedTokenTS < tokenTTL) {
          this.tokenDetected = true;
          this._setToken(storedToken);
        } else {
          setTimeout(() => this.logout());
        }
      } else if (isInsideTgMiniApp()) {
        const webApp = window.Telegram?.WebApp;
        if (!webApp) {
          this.updateState({ detected: true, error: 'Telegram SDK not available' });
          return;
        }
        const user = webApp.initDataUnsafe?.user;
        if (!user?.id) {
          this.updateState({ detected: true, error: 'Telegram user data not available' });
          return;
        }
        (async () => {
          try {
            const data = await this.api.authTelegram({
              payload: {
                initData: webApp.initData,
                hash: webApp.initDataUnsafe.hash,
                telegramChatId: user.id,
              },
              responseType: 'full',
            });
            const token = data?.token || data?.data?.token;
            if (token) {
              this.login(token, {
                tokenDetected: true,
                isLogged: true,
                fullMemberAccess: true,
                viewCabinetMemberAccess: true,
              });
            } else {
              this.updateState({
                detected: true,
                error: data?.error || data?.message || null,
              });
            }
          } catch (e) {
            this.updateState({
              detected: true,
              error: 'Server error',
            });
          }
        })();
      }
    }
  }

  prepareState(dependencies, prevService, newState) {
    super.prepareState(dependencies, prevService, newState);
    if (prevService.api !== dependencies.api) {
      this.requestAccess();
    }
  }

  requestAccess() {
    const pathname =
      typeof window !== 'undefined' && window.location.pathname ? window.location.pathname : '';
    const searchParams =
      typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
    const isVkLinkCallbackInProgress =
      isVkOAuthLinkFlow() && isVkOAuthCallbackWithParams(pathname, searchParams);

    if (isVkLinkCallbackInProgress) {
      return;
    }

    const request = async () => {
      try {
        const data = await this.api.isLoggedIn();
        if (data) {
          const searchParams = new URLSearchParams(window.location.search);
          const urlToken = searchParams.get('token') || searchParams.get('accessToken');
          const isLoginViaLink = !!urlToken;
          
          if (isLoginViaLink && data.isLogged) {
            clearAppStorageKeys();
          }
          
          this.updateState({ detected: true, ...data });
          if (typeof this.onAccessChange === 'function') {
            this.onAccessChange(data);
          }
        }
      } catch (e) {
        this.updateState({
          tokenDetected: false,
          detected: true,
          memberId: null,
          isLogged: false,
          fullMemberAccess: false,
          viewCabinetMemberAccess: false,
        });
      }
    };
    this.api && request();
  }

  matchState(dependencies, newState) {
    const match = super.matchState(dependencies, newState);
    return match;
  }

  hasAnyAccess() {
    return this.isLogged && (this.fullMemberAccess || this.viewCabinetMemberAccess);
  }

  permit(access = {}) {
    return _isMatch(this, access);
  }

  login(token, accessState) {
    const searchParams = new URLSearchParams(window.location.search);
    const urlToken = searchParams.get('token') || searchParams.get('accessToken');
    const isLoginViaLink = !!urlToken;
    
    if (isLoginViaLink) {
      clearAppStorageKeys();
    }
    
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(TOKEN_TS_KEY, Date.now());
    this._setToken(token);
    this.updateState({ detected: true, ...accessState });
  }

  logout() {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(TOKEN_TS_KEY);
    this.updateState({
      tokenDetected: false,
      detected: false,
      memberId: null,
      isLogged: false,
      fullMemberAccess: false,
      viewCabinetMemberAccess: false,
    });
    this._setToken(null);
  }

  _setToken(token) {
    this.api.updateState({ token });
  }
}

export default injectedService(
  {
    name: 'AccessServiceProvider',
    services: ['api'],
  },
  function AccessServiceProvider({ id, children, services: { api }, onAccessChange, tokenTTL }) {
    return (
      <ServiceProvider
        serviceId={id}
        serviceClass={AccessService}
        dependencies={{ api, onAccessChange, tokenTTL }}
      >
        {children}
      </ServiceProvider>
    );
  }
);
